import type {
  AxAIOpenAIEmbedRequest,
  AxAIOpenAIEmbedResponse,
  AxAPI,
} from '@ax-llm/ax/index.js';
import {
  AxAIRefusalError,
  AxAIServiceResponseError,
} from '../../util/apicall.js';
import type { AxAIFeatures } from '../base.js';
import { axResolveServiceTier } from '../service_tier.js';
import type {
  AxAIServiceImpl,
  AxAIServiceOptions,
  AxChatRequest,
  AxChatResponse,
  AxChatResponseResult,
  AxFunctionResultContent,
  AxInternalChatRequest,
  AxInternalEmbedRequest,
  AxModelConfig,
  AxModelInfo,
  AxTokenUsage,
} from '../types.js';
import { axResolveOpenAIPromptCacheKey } from './caching.js';
import { axResolveOpenAIResponsesReasoningEffort } from './effort.js';
import { openAIOSeriesSamplingModelInfo } from './info.js';
import { axIsGPT6Astra, axIsGPT6Family } from './model_family.js';
import { axValidateOpenAIResponseRequest } from './responses_client.js';
import type {
  AxAIOpenAIResponsesCodeInterpreterToolCall,
  AxAIOpenAIResponsesComputerToolCall,
  AxAIOpenAIResponsesConfig,
  AxAIOpenAIResponsesDefineFunctionTool,
  AxAIOpenAIResponsesDefineImageGenerationTool,
  AxAIOpenAIResponsesFileSearchToolCall,
  AxAIOpenAIResponsesFunctionCallItem,
  AxAIOpenAIResponsesImageGenerationToolCall,
  AxAIOpenAIResponsesInputContentPart,
  AxAIOpenAIResponsesInputItem,
  AxAIOpenAIResponsesInputMessageItem,
  AxAIOpenAIResponsesInputReasoningItem,
  AxAIOpenAIResponsesLocalShellToolCall,
  AxAIOpenAIResponsesMCPToolCall,
  AxAIOpenAIResponsesOutputRefusalContentPart,
  AxAIOpenAIResponsesOutputTextContentPart,
  AxAIOpenAIResponsesRealtimeAdapter,
  AxAIOpenAIResponsesRequest,
  AxAIOpenAIResponsesResponse,
  AxAIOpenAIResponsesStreamEvent,
  AxAIOpenAIResponsesToolDefinition,
  AxAIOpenAIResponsesWebSearchToolCall,
  Mutable,
  OpenAIResponsesResponseDelta,
  RequestFunctionDefinition,
  ResponsesReqUpdater,
  UserMessageContentItem,
} from './responses_types.js';
import { axNormalizeOpenAIUsage } from './usage.js';

export class AxAIOpenAIResponsesImpl<
  TModel,
  TEmbedModel, // Kept for interface compatibility, but not used by this impl.
  TResponsesReq extends AxAIOpenAIResponsesRequest<TModel>,
> implements
    AxAIServiceImpl<
      TModel,
      TEmbedModel,
      Readonly<AxAIOpenAIResponsesRequest<TModel>>, // ChatReq (now ResponsesReq)
      Readonly<AxAIOpenAIEmbedRequest<TEmbedModel>>, // EmbedReq
      Readonly<AxAIOpenAIResponsesResponse>, // ChatResp (now ResponsesResp)
      Readonly<OpenAIResponsesResponseDelta>, // ChatRespDelta (now ResponsesRespDelta)
      Readonly<AxAIOpenAIEmbedResponse> // EmbedResp
    >
{
  private tokensUsed: AxTokenUsage | undefined;

  constructor(
    private readonly config: Readonly<
      AxAIOpenAIResponsesConfig<TModel, TEmbedModel>
    >,
    private readonly streamingUsage: boolean, // If /v1/responses supports include_usage for streams
    private readonly options?: Readonly<AxAIServiceOptions>,
    private readonly responsesReqUpdater?: ResponsesReqUpdater<
      TModel,
      TResponsesReq
    >,
    private readonly supportFor?:
      | AxAIFeatures
      | ((model: TModel) => AxAIFeatures),
    private readonly realtime?: AxAIOpenAIResponsesRealtimeAdapter<TModel>,
    private readonly apiKey = ''
  ) {}

  supportsImplicitCaching = (model: TModel): boolean =>
    axIsGPT6Family(model) || (this.config.promptCaching ?? false);

  getTokenUsage(): Readonly<AxTokenUsage> | undefined {
    return this.tokensUsed;
  }

  getModelConfig(): Readonly<AxModelConfig> {
    const { config } = this;
    return {
      maxTokens: config.maxTokens, // maps to max_output_tokens
      temperature: config.temperature,
      presencePenalty: config.presencePenalty,
      frequencyPenalty: config.frequencyPenalty,
      stopSequences: config.stopSequences, // /v1/responses uses 'truncation' or relies on item structure
      topP: config.topP,
      // n: config.n, // Not a direct parameter in /v1/responses
      stream: config.stream,
    };
  }

  /** OpenAI's info for an o-series name, for profiles without model info. */
  samplingModelInfo(model: TModel): Readonly<AxModelInfo> | undefined {
    return openAIOSeriesSamplingModelInfo(String(model));
  }

  /** The `reasoning.effort` createChatReq will send for this model. */
  resolveReasoningEffort(
    model: TModel,
    config: Readonly<AxAIServiceOptions>
  ): AxAIOpenAIResponsesConfig<TModel, TEmbedModel>['reasoningEffort'] {
    const budget = config.thinkingTokenBudget;
    if (!budget) return this.config.reasoningEffort;
    if (budget === 'highest' && this.config.highestReasoningEffort) {
      return this.config.highestReasoningEffort;
    }
    const effort = axResolveOpenAIResponsesReasoningEffort(model, budget);
    return budget === 'none'
      ? effort
      : (this.config.reasoningEffortMap?.[budget] ?? effort);
  }

  private mapInternalContentToResponsesInput(
    content: ReadonlyArray<UserMessageContentItem>, // Expects an array of content items, string case handled by caller
    role: 'user' | 'assistant'
  ): ReadonlyArray<AxAIOpenAIResponsesInputContentPart> {
    const mappedParts: Mutable<AxAIOpenAIResponsesInputContentPart>[] = [];

    for (const part of content) {
      const cacheControl =
        this.config.promptCaching && part.cache
          ? { cache_control: { type: 'ephemeral' as const } }
          : {};
      if (part.type === 'text') {
        if (role === 'assistant') {
          mappedParts.push({
            type: 'output_text',
            text: part.text,
          } as unknown as AxAIOpenAIResponsesInputContentPart);
        } else {
          mappedParts.push({
            type: 'input_text',
            text: part.text,
            ...cacheControl,
          });
        }
        continue;
      }

      if (role === 'assistant') {
        // Assistant message content in input must be output types only. Skip non-text parts.
        continue;
      }

      if (part.type === 'image') {
        const url = `data:${part.mimeType};base64,${part.image}`;
        mappedParts.push({
          type: 'input_image',
          image_url: url,
          detail: part.details ?? 'auto',
          ...cacheControl,
        } as const);
        continue;
      }
      if (part.type === 'audio') {
        mappedParts.push({
          type: 'input_audio',
          input_audio: {
            data: part.data,
            format: part.format,
          },
          ...cacheControl,
        } as const);
        continue;
      }
      if (part.type === 'file') {
        if (part.mimeType.startsWith('image/')) {
          mappedParts.push({
            type: 'input_image',
            image_url:
              part.fileUri ?? `data:${part.mimeType};base64,${part.data ?? ''}`,
            detail: 'auto',
            ...cacheControl,
          });
        } else if (part.fileUri && part.mimeType.startsWith('video/')) {
          mappedParts.push({
            type: 'input_video',
            video_url: part.fileUri,
            ...cacheControl,
          });
        } else {
          mappedParts.push({
            type: 'input_file',
            ...(part.fileUri
              ? { file_url: part.fileUri }
              : {
                  file_data: `data:${part.mimeType};base64,${part.data ?? ''}`,
                }),
            filename: part.filename,
            ...cacheControl,
          });
        }
        continue;
      }
      if (part.type === 'url') {
        mappedParts.push({
          type: 'input_text',
          text:
            part.cachedContent ??
            [part.title, part.description, part.url].filter(Boolean).join('\n'),
          ...cacheControl,
        });
        continue;
      }

      const ExhaustiveCheck: never = part;
      throw new Error(
        `Unsupported content part: ${JSON.stringify(ExhaustiveCheck)}`
      );
    }

    return mappedParts as ReadonlyArray<AxAIOpenAIResponsesInputContentPart>;
  }

  private mapFunctionResultContent(
    content: AxFunctionResultContent | undefined,
    fallback: string
  ): string | ReadonlyArray<AxAIOpenAIResponsesInputContentPart> {
    if (!content?.length) return fallback;
    return content.map((part) => {
      if (part.type === 'text') {
        return { type: 'input_text' as const, text: part.text };
      }
      if (part.type === 'image') {
        return {
          type: 'input_image' as const,
          image_url: `data:${part.mimeType};base64,${part.image}`,
          detail: 'auto' as const,
        };
      }
      if (part.type === 'audio') {
        return {
          type: 'input_audio' as const,
          input_audio: { data: part.data },
        };
      }
      if (part.type === 'file') {
        return {
          type: 'input_file' as const,
          file_data: `data:${part.mimeType};base64,${part.data}`,
          filename: part.filename,
        };
      }
      return {
        type: 'input_text' as const,
        text:
          part.cachedContent ??
          [part.title, part.description, part.url].filter(Boolean).join('\n'),
      };
    });
  }

  private createResponsesReqInternalInput(
    chatPrompt: ReadonlyArray<AxChatRequest<TModel>['chatPrompt'][number]>,
    excludeSystemMessages = false,
    model: TModel = this.config.model
  ): ReadonlyArray<AxAIOpenAIResponsesInputItem> {
    // Map from AxChatPromptItemType roles to AxAIOpenAI /v1/responses API roles:
    // - 'system' -> 'system' (may be skipped if excludeSystemMessages is true)
    // - 'user' -> 'user'
    // - 'assistant' -> 'assistant'
    // - 'function' -> Special handling for function call outputs (different structure)
    //
    // Note: AxAIOpenAI's /v1/responses API also supports a 'developer' role that isn't
    // currently mapped from our AxChatPromptItemType structure.

    const items: Mutable<AxAIOpenAIResponsesInputItem>[] = [];
    for (const msg of chatPrompt) {
      if (excludeSystemMessages && msg.role === 'system') {
        continue; // Skip system messages if they are handled by top-level 'instructions'
      }

      let mappedContent:
        | string
        | ReadonlyArray<AxAIOpenAIResponsesInputContentPart>;
      // Type guard for content based on role
      if (
        msg.role === 'system' ||
        msg.role === 'user' ||
        (msg.role === 'assistant' && msg.content)
      ) {
        if (typeof msg.content === 'string') {
          if (msg.role === 'system') {
            mappedContent =
              this.config.promptCaching && msg.cache
                ? [
                    {
                      type: 'input_text',
                      text: msg.content,
                      cache_control: { type: 'ephemeral' },
                    },
                  ]
                : msg.content;
          } else if (msg.role === 'assistant') {
            mappedContent = [
              { type: 'output_text', text: msg.content },
            ] as ReadonlyArray<AxAIOpenAIResponsesInputContentPart>;
          } else {
            mappedContent = [
              { type: 'input_text', text: msg.content },
            ] as ReadonlyArray<AxAIOpenAIResponsesInputContentPart>;
          }
        } else if (Array.isArray(msg.content)) {
          mappedContent = this.mapInternalContentToResponsesInput(
            msg.content as ReadonlyArray<UserMessageContentItem>,
            msg.role === 'assistant' ? 'assistant' : 'user'
          );
        } else {
          // Handle cases where content might be undefined for assistant, or unexpected type
          if (msg.role === 'assistant' && !msg.content && msg.functionCalls) {
            // This is fine, assistant message can be just functionCalls
          } else {
            throw new Error(`Invalid content type for role ${msg.role}`);
          }
          mappedContent = ''; // Default or skip
        }
      } else if (msg.role === 'function') {
        // Function role does not have 'content' in the same way, it has 'result'
        mappedContent = ''; // Placeholder, not directly used for content field in function_call_output
      } else {
        mappedContent = ''; // Default for roles that might not have content or are handled differently
      }

      switch (msg.role) {
        case 'system': // Will be skipped if excludeSystemMessages is true
          items.push({
            type: 'message',
            role: 'system',
            content: mappedContent as string,
          });
          break;
        case 'user':
          if (
            this.config.promptCaching &&
            msg.cache &&
            Array.isArray(mappedContent) &&
            mappedContent.length > 0
          ) {
            const last = mappedContent[mappedContent.length - 1]!;
            mappedContent = [
              ...mappedContent.slice(0, -1),
              { ...last, cache_control: { type: 'ephemeral' } },
            ];
          }
          items.push({
            type: 'message',
            role: 'user',
            content: mappedContent,
            name: msg.name,
          });
          break;
        case 'assistant':
          if (msg.thought || msg.thoughtBlocks?.length) {
            const commentaryBlocks = msg.thoughtBlocks?.filter(
              (block) => block.phase === 'commentary'
            );
            for (const block of commentaryBlocks ?? []) {
              items.push({
                type: 'message',
                role: 'assistant',
                phase: 'commentary',
                content: [{ type: 'output_text', text: block.data }],
              });
            }
            const replayableBlocks = msg.thoughtBlocks?.filter(
              (block) =>
                (block.encryptedContent || (block.encrypted && block.data)) &&
                block.phase !== 'commentary'
            );
            if (replayableBlocks?.length) {
              for (const block of replayableBlocks) {
                items.push({
                  type: 'reasoning',
                  id: block.id ?? block.signature,
                  summary:
                    block.summary ||
                    (block.encryptedContent &&
                      (block.data || !axIsGPT6Astra(model)))
                      ? [
                          {
                            type: 'summary_text',
                            text: block.summary ?? block.data,
                          },
                        ]
                      : [],
                  encrypted_content: block.encryptedContent ?? block.data,
                } satisfies AxAIOpenAIResponsesInputReasoningItem);
              }
            } else {
              const thought =
                msg.thought ??
                msg.thoughtBlocks?.map((block) => block.data).join('');
              if (thought && !commentaryBlocks?.length) {
                items.push({
                  type: 'reasoning',
                  content: thought,
                } satisfies AxAIOpenAIResponsesInputReasoningItem);
              }
            }
          }
          if (msg.images?.length) {
            for (const image of msg.images) {
              if (!image.id) {
                throw new Error(
                  'Responses image replay requires the provider image id'
                );
              }
              items.push({
                type: 'image_generation_call',
                id: image.id,
                status: 'completed',
                result: null,
              });
            }
          }
          if (msg.content || msg.functionCalls) {
            // Assistant can have content, functionCalls, or both
            const assistantMessage: Mutable<AxAIOpenAIResponsesInputMessageItem> =
              {
                type: 'message',
                role: 'assistant',
                content: '',
                phase: msg.phase,
              }; // Start with empty content
            if (msg.content) {
              assistantMessage.content = mappedContent;
            }
            if (msg.name) {
              assistantMessage.name = msg.name;
            }
            // If only function calls, content might remain empty or not be applicable in the same way for AxAIOpenAI item
            // AxAIOpenAI /v1/responses expects assistant messages with tool calls to be structured carefully.
            // For now, pushing the textual content if present. Tool calls are separate items.
            if (msg.content)
              items.push(
                assistantMessage as AxAIOpenAIResponsesInputMessageItem
              );

            if (msg.functionCalls) {
              for (const call of msg.functionCalls) {
                items.push({
                  type: 'function_call',
                  call_id: call.id,
                  name: call.function.name,
                  arguments:
                    typeof call.function.params === 'object'
                      ? JSON.stringify(call.function.params)
                      : call.function.params || '',
                });
              }
            }
          }
          break;
        case 'function': // This is a tool result
          items.push({
            type: 'function_call_output',
            call_id: msg.functionId!,
            output: this.mapFunctionResultContent(msg.content, msg.result),
          });
          break;
        default: {
          // Fix for any type
          const invalidRole = (msg as { role: string }).role;
          throw new Error(`Invalid role in chat prompt: ${invalidRole}`);
        }
      }
    }
    return items as ReadonlyArray<AxAIOpenAIResponsesInputItem>;
  }

  createChatReq(
    req: Readonly<AxInternalChatRequest<TModel>>,
    config: Readonly<AxAIServiceOptions>
  ): [Readonly<AxAPI>, Readonly<AxAIOpenAIResponsesRequest<TModel>>] {
    const model = req.model;
    const apiConfig: AxAPI = { name: '/responses' };
    const features =
      typeof this.supportFor === 'function'
        ? this.supportFor(model)
        : this.supportFor;
    const serviceTier = axResolveServiceTier({
      requested:
        config.serviceTier ??
        this.options?.serviceTier ??
        this.config.serviceTier,
      supported: features?.serviceTiers,
      mapping: {
        auto: 'auto',
        standard: 'default',
        flex: 'flex',
        priority: 'priority',
      },
      provider: 'OpenAI-compatible Responses',
      model: String(model),
    });

    let instructionsFromPrompt: string | null = null;
    let systemMessageFoundAndUsed = false;
    if (req.chatPrompt) {
      for (const item of req.chatPrompt) {
        if (
          item.role === 'system' &&
          typeof item.content === 'string' &&
          !this.config.promptCaching
        ) {
          instructionsFromPrompt = item.content;
          systemMessageFoundAndUsed = true;
          break;
        }
      }
    }

    const finalInstructions =
      instructionsFromPrompt ?? this.config.systemPrompt ?? null;

    const functionTools = req.functions?.map(
      (
        v: Readonly<RequestFunctionDefinition>
      ): AxAIOpenAIResponsesDefineFunctionTool => ({
        type: 'function' as const,
        name: v.name,
        description: v.description,
        parameters: v.parameters ?? {},
      })
    );
    const imageTool: AxAIOpenAIResponsesDefineImageGenerationTool | undefined =
      this.config.imageGeneration
        ? {
            type: 'image_generation',
            size: this.config.imageGeneration.size,
            output_format: this.config.imageGeneration.outputFormat,
            enable_image_search: this.config.imageGeneration.enableImageSearch,
            enable_web_search: this.config.imageGeneration.enableWebSearch,
            enable_shell: this.config.imageGeneration.enableShell,
            reasoning_strength: this.config.imageGeneration.reasoningStrength,
          }
        : undefined;
    const tools: ReadonlyArray<AxAIOpenAIResponsesToolDefinition> | undefined =
      imageTool || functionTools?.length
        ? [...(imageTool ? [imageTool] : []), ...(functionTools ?? [])]
        : undefined;

    // Set include field based on showThoughts option, but override if thinkingTokenBudget is 'none'
    const includeFields: Array<
      'message.input_image.image_url' | 'reasoning.encrypted_content'
    > =
      // | 'computer_call_output.output.image_url'
      // | 'reasoning.encrypted_content'
      // | 'code_interpreter_call.outputs'
      this.config.includeEncryptedReasoning
        ? ['reasoning.encrypted_content']
        : [];

    let reasoningSummary = this.config.reasoningSummary;

    if (!config?.showThoughts) {
      reasoningSummary = undefined;
    } else if (!reasoningSummary) {
      reasoningSummary = 'auto';
    }

    if (
      config?.thinkingTokenBudget === 'none' &&
      this.config.rejectReasoningNone
    ) {
      throw new Error('This provider does not support reasoning level none');
    }
    const reasoningEffort = this.resolveReasoningEffort(model, config ?? {});

    const mutableReq: Mutable<AxAIOpenAIResponsesRequest<TModel>> = {
      model,
      input: '', // Will be set below
      instructions: finalInstructions,
      tools: tools?.length ? tools : undefined,
      tool_choice:
        req.functionCall === 'none' ||
        req.functionCall === 'auto' ||
        req.functionCall === 'required'
          ? req.functionCall
          : typeof req.functionCall === 'object' && req.functionCall.function
            ? { type: 'function', name: req.functionCall.function.name }
            : undefined,
      // Model info decides which sampling parameters the model accepts; the
      // base layer has already removed the rest from modelConfig, which also
      // carries the AI's own config.
      ...{
        ...(req.modelConfig?.temperature !== undefined
          ? { temperature: req.modelConfig.temperature }
          : {}),
        ...(req.modelConfig?.topP !== undefined
          ? { top_p: req.modelConfig.topP }
          : {}),
        presence_penalty: req.modelConfig?.presencePenalty ?? undefined,
        frequency_penalty: req.modelConfig?.frequencyPenalty ?? undefined,
        max_output_tokens:
          req.modelConfig?.maxTokens ?? this.config.maxTokens ?? undefined,
      },
      stream: req.modelConfig?.stream ?? this.config.stream ?? false, // Sourced from modelConfig or global config
      // Optional fields from AxAIOpenAIResponsesRequest that need to be in Mutable for initialization
      background: undefined,
      include: includeFields.length > 0 ? includeFields : undefined,
      metadata: undefined,
      parallel_tool_calls: this.config.parallelToolCalls,
      previous_response_id: undefined,
      ...(reasoningEffort
        ? {
            reasoning: {
              effort: reasoningEffort,
              // Summarizing reasoning that was explicitly disabled is
              // contradictory, so 'none' carries no summary.
              ...(reasoningEffort === 'none'
                ? {}
                : { summary: reasoningSummary }),
            },
          }
        : {}),
      service_tier: serviceTier,
      store: this.config.store,
      text: undefined,
      truncation: undefined,
      user: this.config.user,
      seed: this.config.seed,
      prompt_cache_key: axResolveOpenAIPromptCacheKey(config, this.options),
      prompt_cache_retention:
        config.promptCacheRetention ??
        this.options?.promptCacheRetention ??
        this.config.promptCacheRetention,
    };

    // Populate from this.config if properties exist on AxAIOpenAIConfig
    if (this.config.user) mutableReq.user = this.config.user;
    if (this.config.parallelToolCalls !== undefined)
      mutableReq.parallel_tool_calls = this.config.parallelToolCalls;
    if (req.responseFormat) {
      mutableReq.text = {
        format:
          req.responseFormat.type === 'json_schema'
            ? {
                type: 'json_schema',
                ...req.responseFormat.schema,
              }
            : { type: req.responseFormat.type as 'text' | 'json_object' },
      };
    } else if (this.config.responseFormat)
      mutableReq.text = {
        format: {
          type: this.config.responseFormat as
            | 'text'
            | 'json_object'
            | 'json_schema',
        },
      };
    if (this.config.seed) mutableReq.seed = this.config.seed;
    // TODO: Check AxAIOpenAIConfig for other fields like store, background, include, metadata, service_tier, truncation

    const inputItems = req.chatPrompt
      ? this.createResponsesReqInternalInput(
          req.chatPrompt,
          systemMessageFoundAndUsed,
          model
        )
      : [];

    if (inputItems.length > 0) {
      mutableReq.input = inputItems;
    } else if (
      req.chatPrompt &&
      req.chatPrompt.length === 1 &&
      req.chatPrompt[0]?.role === 'user' &&
      req.chatPrompt[0]?.content &&
      typeof req.chatPrompt[0].content === 'string' &&
      !finalInstructions
    ) {
      // Fallback to simple string input if only one user message and no instructions
      mutableReq.input = req.chatPrompt[0].content;
    } else if (inputItems.length === 0 && !finalInstructions) {
      throw new Error('Responses API request must have input or instructions.');
    }

    // The effort was resolved once above; drop the object outright when none
    // survived so an effortless `reasoning` never reaches the wire.
    if (!mutableReq.reasoning?.effort) {
      mutableReq.reasoning = undefined;
    }

    let finalReqToProcess: Readonly<AxAIOpenAIResponsesRequest<TModel>> =
      mutableReq as Readonly<AxAIOpenAIResponsesRequest<TModel>>;

    if (this.responsesReqUpdater) {
      finalReqToProcess = this.responsesReqUpdater(
        finalReqToProcess as Readonly<TResponsesReq>,
        config
      );
    }

    if (axIsGPT6Family(model)) {
      if (
        config.contextCache !== undefined ||
        req.chatPrompt.some((m) => 'cache' in m && m.cache) ||
        req.functions?.some((f) => f.cache)
      ) {
        const marked = Array.isArray(finalReqToProcess.input)
          ? finalReqToProcess.input.map((item) => {
              if (
                typeof item !== 'object' ||
                item.type !== 'message' ||
                !item.content
              )
                return item;
              const content =
                typeof item.content === 'string'
                  ? [
                      {
                        type:
                          item.role === 'assistant'
                            ? 'output_text'
                            : 'input_text',
                        text: item.content,
                      },
                    ]
                  : item.content;
              return {
                ...item,
                content: content.map((part: object, index: number) =>
                  index === content.length - 1
                    ? { ...part, prompt_cache_breakpoint: { mode: 'explicit' } }
                    : part
                ),
              };
            })
          : finalReqToProcess.input;
        finalReqToProcess = {
          ...finalReqToProcess,
          input: marked as typeof finalReqToProcess.input,
          prompt_cache_options: { mode: 'explicit', ttl: '30m' },
          // The call's key, else the service's, as on every Responses request.
          prompt_cache_key: axResolveOpenAIPromptCacheKey(config, this.options),
        };
      }
      finalReqToProcess = axValidateOpenAIResponseRequest(finalReqToProcess);
    }
    if (this.realtime?.shouldUse(String(model))) {
      if (req.functions?.length) {
        throw new Error(
          'Meta Voice realtime transcription does not support tools'
        );
      }
      const audio = req.modelConfig?.audio ?? this.config.audio;
      let sampleRate = audio?.input?.sampleRate;
      let channels = audio?.input?.channels;
      for (const message of req.chatPrompt ?? []) {
        if (message.role !== 'user' || !Array.isArray(message.content))
          continue;
        for (const part of message.content) {
          if (part.type !== 'audio') continue;
          if (part.sampleRate !== undefined) {
            if (sampleRate !== undefined && sampleRate !== part.sampleRate) {
              throw new Error('Conflicting realtime audio sample rates');
            }
            sampleRate = part.sampleRate;
          }
          if (part.channels !== undefined) {
            if (channels !== undefined && channels !== part.channels) {
              throw new Error('Conflicting realtime audio channel counts');
            }
            channels = part.channels;
          }
        }
      }
      const realtimeApi = this.realtime.createApi({
        model,
        request: finalReqToProcess,
        apiKey: this.apiKey,
        audio: { ...audio, input: { ...audio?.input, sampleRate, channels } },
        webSocket: config.webSocket ?? this.options?.webSocket,
        abortSignal: config.abortSignal ?? this.options?.abortSignal,
        turnTimeoutMs:
          req.modelConfig?.audio?.live?.turnTimeoutMs ??
          this.config.audio?.live?.turnTimeoutMs,
        sessionId: config.sessionId ?? this.options?.sessionId,
      });
      apiConfig.name = realtimeApi.name;
      apiConfig.localCall = realtimeApi.localCall;
    }

    return [apiConfig, finalReqToProcess];
  }

  // Create Chat Response from /v1/responses (non-streaming)
  createChatResp(
    resp: Readonly<AxAIOpenAIResponsesResponse>
  ): Readonly<AxChatResponse> {
    const { id, output, usage } = resp;
    const metaResponse = resp as AxAIOpenAIResponsesResponse & {
      meta_session_id?: string;
      meta_results?: AxChatResponseResult[];
    };

    this.tokensUsed = axNormalizeOpenAIUsage(
      usage,
      resp.service_tier_used ?? resp.service_tier
    );
    if (metaResponse.meta_results) {
      return {
        results: metaResponse.meta_results,
        remoteId: id,
        remoteSessionId: metaResponse.meta_session_id,
      };
    }

    const currentResult: Partial<AxChatResponseResult> = {};

    // /v1/responses returns one output item per parallel tool call, so these
    // have to accumulate across the loop instead of overwriting each other.
    const functionCalls: NonNullable<AxChatResponseResult['functionCalls']> =
      [];

    for (const item of output ?? []) {
      switch (item.type) {
        case 'message':
          currentResult.id = item.id;
          {
            const text = contentToText(item.content, id);
            if (item.phase === 'commentary') {
              currentResult.thought = `${currentResult.thought ?? ''}${text}`;
              currentResult.thoughtBlocks = [
                ...(currentResult.thoughtBlocks ?? []),
                {
                  data: text,
                  encrypted: false,
                  id: item.id,
                  phase: 'commentary',
                },
              ];
            } else {
              currentResult.content = text;
              currentResult.phase = item.phase;
            }
          }
          currentResult.finishReason =
            item.status === 'completed' ? 'stop' : 'content_filter';
          // Extract annotations from output_text parts
          currentResult.citations = extractAnnotationsFromContent(item.content);
          break;

        case 'reasoning':
          currentResult.id = item.id;
          {
            const thought = reasoningItemToText(item);
            if (thought) {
              currentResult.thought = currentResult.thought
                ? `${currentResult.thought}${thought}`
                : thought;
            }
            if (thought || item.encrypted_content) {
              currentResult.thoughtBlocks = [
                ...(currentResult.thoughtBlocks ?? []),
                {
                  data: thought,
                  encrypted: Boolean(item.encrypted_content),
                  id: item.id,
                  summary: item.summary?.map((part) => part.text).join('\n'),
                  encryptedContent: item.encrypted_content ?? undefined,
                },
              ];
            }
          }
          break;

        case 'file_search_call':
          currentResult.id = item.id;
          currentResult.functionCalls = [
            {
              id: item.id,
              type: 'function' as const,
              function: {
                name: 'file_search',
                params: {
                  queries: item.queries,
                  results: item.results,
                },
              },
            },
          ];
          currentResult.finishReason = 'function_call';
          break;
        case 'web_search_call':
          currentResult.id = item.id;
          currentResult.functionCalls = [
            {
              id: item.id,
              type: 'function' as const,
              function: {
                name: 'web_search',
                params: {
                  queries: item.queries,
                },
              },
            },
          ];
          currentResult.finishReason = 'function_call';
          break;
        case 'computer_call':
          currentResult.id = item.id;
          currentResult.functionCalls = [
            {
              id: item.id,
              type: 'function' as const,
              function: {
                name: 'computer_use',
                params: {
                  action: item.action,
                },
              },
            },
          ];
          currentResult.finishReason = 'function_call';
          break;
        case 'code_interpreter_call':
          currentResult.id = item.id;
          currentResult.functionCalls = [
            {
              id: item.id,
              type: 'function' as const,
              function: {
                name: 'code_interpreter',
                params: {
                  code: item.code,
                  results: item.results,
                },
              },
            },
          ];
          currentResult.finishReason = 'function_call';
          break;
        case 'image_generation_call':
          currentResult.id = item.id;
          if (item.result) {
            currentResult.images = [
              ...(currentResult.images ?? []),
              normalizeImageOutput(
                item.id,
                item.result,
                this.config.imageGeneration?.outputFormat ??
                  this.config.defaultImageOutputFormat
              ),
            ];
          }
          currentResult.finishReason = 'stop';
          break;
        case 'local_shell_call':
          currentResult.id = item.id;
          currentResult.functionCalls = [
            {
              id: item.id,
              type: 'function' as const,
              function: {
                name: 'local_shell',
                params: {
                  action: item.action,
                },
              },
            },
          ];
          currentResult.finishReason = 'function_call';
          break;
        case 'mcp_call':
          currentResult.id = item.id;
          currentResult.functionCalls = [
            {
              id: item.id,
              type: 'function' as const,
              function: {
                name: 'mcp',
                params: {
                  name: item.name,
                  args: item.args,
                  serverLabel: item.server_label,
                  output: item.output,
                  error: item.error,
                },
              },
            },
          ];
          currentResult.finishReason = 'function_call';
          break;
        case 'function_call':
          currentResult.id = item.id;
          functionCalls.push({
            id: item.call_id,
            type: 'function' as const,
            function: {
              name: item.name,
              params: item.arguments,
            },
          });
          currentResult.finishReason = 'function_call';
          break;
      }
    }

    if (functionCalls.length > 0) {
      currentResult.functionCalls = functionCalls;
    }

    return {
      results: [{ ...currentResult, index: 0 }],
      remoteId: id,
    };
  }

  // Create Chat Stream Response from /v1/responses stream events
  createChatStreamResp = (
    streamEvent: Readonly<OpenAIResponsesResponseDelta>,
    state: object
  ): Readonly<AxChatResponse> => {
    // Handle new streaming event format
    const event = streamEvent as AxAIOpenAIResponsesStreamEvent;
    const sstate = state as {
      remoteId?: string;
      functionCallIds?: Map<string, string>;
      phases?: Map<string, 'commentary' | 'final_answer'>;
    };

    // Create a basic result structure
    const baseResult: AxChatResponseResult = {
      index: 0,
      id: '',
      content: '',
      finishReason: 'stop',
    };

    let remoteId =
      typeof (event as { response?: { id?: unknown } }).response?.id ===
      'string'
        ? (event as { response: { id: string } }).response.id
        : undefined;

    switch (event.type) {
      case 'response.created':
      case 'response.in_progress':
      case 'response.queued':
        // Response lifecycle events - return empty content with metadata
        remoteId = event.response.id;
        baseResult.id = `${event.response.id}_res_0`;
        break;

      case 'response.output_item.added':
        // New output item added
        switch (event.item.type) {
          case 'message':
            baseResult.id = event.item.id;
            if (event.item.phase) {
              sstate.phases ??= new Map();
              sstate.phases.set(event.item.id, event.item.phase);
            }
            baseResult.phase = event.item.phase;
            baseResult.content = contentToText(
              event.item.content,
              event.item.id
            );
            if (event.item.phase === 'commentary' && baseResult.content) {
              baseResult.thought = baseResult.content;
              baseResult.thoughtBlocks = [
                {
                  data: baseResult.content,
                  encrypted: false,
                  id: event.item.id,
                  phase: 'commentary',
                },
              ];
              baseResult.content = '';
            }
            baseResult.citations = extractAnnotationsFromContent(
              event.item.content
            );
            break;
          case 'function_call':
            {
              const functionCallItem =
                event.item as AxAIOpenAIResponsesFunctionCallItem;
              sstate.functionCallIds ??= new Map();
              sstate.functionCallIds.set(
                functionCallItem.id,
                functionCallItem.call_id
              );
              baseResult.id = event.item.id;
              baseResult.functionCalls = [
                {
                  id: functionCallItem.call_id,
                  type: 'function' as const,
                  function: {
                    name: functionCallItem.name,
                    params: functionCallItem.arguments,
                  },
                },
              ];
            }
            break;
          case 'file_search_call':
            {
              const fileSearchItem =
                event.item as AxAIOpenAIResponsesFileSearchToolCall;
              baseResult.id = event.item.id;
              baseResult.functionCalls = [
                {
                  id: fileSearchItem.id,
                  type: 'function' as const,
                  function: {
                    name: 'file_search',
                    params: {
                      queries: fileSearchItem.queries || [],
                      results: fileSearchItem.results?.map((r) => ({
                        fileId: r.file_id,
                        filename: r.filename,
                        score: r.score,
                        text: r.text,
                        attributes: r.attributes,
                      })),
                    },
                  },
                },
              ];
            }
            break;
          case 'web_search_call':
            {
              const webSearchItem =
                event.item as AxAIOpenAIResponsesWebSearchToolCall;
              baseResult.id = event.item.id;
              baseResult.functionCalls = [
                {
                  id: webSearchItem.id,
                  type: 'function' as const,
                  function: {
                    name: 'web_search',
                    params: {
                      queries: webSearchItem.queries || [],
                    },
                  },
                },
              ];
            }
            break;
          case 'computer_call':
            {
              const computerItem =
                event.item as AxAIOpenAIResponsesComputerToolCall;
              baseResult.id = event.item.id;
              baseResult.functionCalls = [
                {
                  id: computerItem.id,
                  type: 'function' as const,
                  function: {
                    name: 'computer_use',
                    params: {
                      action: computerItem.action || {},
                    },
                  },
                },
              ];
            }
            break;
          case 'code_interpreter_call':
            {
              const codeItem =
                event.item as AxAIOpenAIResponsesCodeInterpreterToolCall;
              baseResult.id = event.item.id;
              baseResult.functionCalls = [
                {
                  id: codeItem.id,
                  type: 'function' as const,
                  function: {
                    name: 'code_interpreter',
                    params: {
                      code: codeItem.code || '',
                      results: codeItem.results,
                    },
                  },
                },
              ];
            }
            break;
          case 'image_generation_call':
            {
              const imageItem =
                event.item as AxAIOpenAIResponsesImageGenerationToolCall;
              baseResult.id = event.item.id;
              if (imageItem.result) {
                baseResult.images = [
                  normalizeImageOutput(
                    imageItem.id,
                    imageItem.result,
                    this.config.imageGeneration?.outputFormat ??
                      this.config.defaultImageOutputFormat
                  ),
                ];
              }
            }
            break;
          case 'local_shell_call':
            {
              const shellItem =
                event.item as AxAIOpenAIResponsesLocalShellToolCall;
              baseResult.id = event.item.id;
              baseResult.functionCalls = [
                {
                  id: shellItem.id,
                  type: 'function' as const,
                  function: {
                    name: 'local_shell',
                    params: {
                      action: shellItem.action || {},
                    },
                  },
                },
              ];
            }
            break;
          case 'mcp_call':
            {
              const mcpItem = event.item as AxAIOpenAIResponsesMCPToolCall;
              baseResult.id = event.item.id;
              baseResult.functionCalls = [
                {
                  id: mcpItem.id,
                  type: 'function' as const,
                  function: {
                    name: 'mcp',
                    params: {
                      name: mcpItem.name || '',
                      args: mcpItem.args || '',
                      serverLabel: mcpItem.server_label || '',
                      output: mcpItem.output,
                      error: mcpItem.error,
                    },
                  },
                },
              ];
            }
            break;
          case 'reasoning': {
            if (event.item.encrypted_content) break;
            baseResult.id = event.item.id;
            const thought = reasoningItemToText(event.item);
            if (thought) baseResult.thought = thought;
            if (thought || event.item.encrypted_content) {
              baseResult.thoughtBlocks = [
                {
                  data: thought,
                  encrypted: Boolean(event.item.encrypted_content),
                  id: event.item.id,
                  summary: event.item.summary
                    ?.map((part) => part.text)
                    .join('\n'),
                  encryptedContent: event.item.encrypted_content ?? undefined,
                },
              ];
            }
            break;
          }
        }
        break;

      case 'response.content_part.added':
        // Content part added - return the initial text if any
        baseResult.id = event.item_id;
        baseResult.content = contentToText([event.part], event.item_id);
        baseResult.citations = extractAnnotationsFromContent([
          event.part as any,
        ]);
        break;

      case 'response.output_text.delta':
        // Text delta - return just the delta content
        baseResult.id = event.item_id;
        baseResult.name = streamEvent.speaker;
        baseResult.audioProcessedMs = streamEvent.audioProcessedMs;
        baseResult.transcript = streamEvent.transcript;
        if (streamEvent.transcript) baseResult.finishReason = undefined;
        if (sstate.phases?.get(event.item_id) === 'commentary') {
          baseResult.thought = event.delta;
          baseResult.thoughtBlocks = [
            {
              data: event.delta,
              encrypted: false,
              id: event.item_id,
              phase: 'commentary',
            },
          ];
        } else {
          baseResult.content = event.delta;
          baseResult.phase = sstate.phases?.get(event.item_id);
        }
        break;

      case 'response.output_text.done':
        break;

      case 'response.function_call_arguments.delta':
        // Function call arguments delta - return delta with empty name
        {
          const functionCallId =
            sstate.functionCallIds?.get(event.item_id) ?? event.item_id;
          baseResult.id = event.item_id;
          baseResult.functionCalls = [
            {
              id: functionCallId,
              type: 'function' as const,
              function: {
                name: '',
                params: event.delta,
              },
            },
          ];
        }
        break;

      // case 'response.function_call_arguments.done':
      //     // Function call arguments done - don't return function calls here
      //     // The mergeFunctionCalls will handle combining name and arguments
      //     baseResult.id = event.item_id
      //     baseResult.finishReason = 'function_call'
      //     break

      case 'response.reasoning_summary_text.delta':
        // Reasoning summary delta
        baseResult.id = event.item_id;
        baseResult.thought = event.delta;
        baseResult.thoughtBlocks = [
          {
            data: event.delta,
            encrypted: false,
            ...(this.config.includeEncryptedReasoning
              ? { id: event.item_id }
              : {}),
          },
        ];
        break;

      case 'response.reasoning_summary_text.done':
      case 'response.reasoning_summary.done':
        baseResult.id = event.item_id;
        baseResult.thought = event.text;
        baseResult.thoughtBlocks = [
          {
            data: event.text,
            encrypted: false,
            ...(this.config.includeEncryptedReasoning
              ? { id: event.item_id, summary: event.text }
              : {}),
          },
        ];
        break;

      case 'response.reasoning_text.delta':
        baseResult.id = event.item_id;
        baseResult.thought = event.delta;
        baseResult.thoughtBlocks = [
          {
            data: event.delta,
            encrypted: false,
            ...(this.config.includeEncryptedReasoning
              ? { id: event.item_id }
              : {}),
          },
        ];
        break;

      case 'response.reasoning_text.done':
        baseResult.id = event.item_id;
        baseResult.thought = event.text;
        baseResult.thoughtBlocks = [
          {
            data: event.text,
            encrypted: false,
            ...(this.config.includeEncryptedReasoning
              ? { id: event.item_id, summary: event.text }
              : {}),
          },
        ];
        break;

      // File search tool events
      case 'response.file_search_call.in_progress':
      case 'response.file_search_call.searching':
        baseResult.id = event.item_id;
        baseResult.finishReason = 'function_call';
        break;

      case 'response.file_search_call.completed':
        baseResult.id = event.item_id;
        baseResult.finishReason = 'function_call';
        break;

      // Web search tool events
      case 'response.web_search_call.in_progress':
      case 'response.web_search_call.searching':
        baseResult.id = event.item_id;
        baseResult.finishReason = 'function_call';
        break;

      case 'response.web_search_call.completed':
        baseResult.id = event.item_id;
        baseResult.finishReason = 'function_call';
        break;

      // Image generation tool events
      case 'response.image_generation_call.in_progress':
      case 'response.image_generation_call.generating':
        baseResult.id = event.item_id;
        baseResult.finishReason = undefined;
        break;

      case 'response.image_generation_call.completed':
        baseResult.id = event.item_id;
        baseResult.finishReason = 'stop';
        break;

      case 'response.image_generation_call.partial_image':
        baseResult.id = event.item_id;
        baseResult.finishReason = undefined;
        baseResult.images = [
          {
            id: event.item_id,
            data: event.partial_image_b64,
            mimeType: imageMimeType(
              this.config.imageGeneration?.outputFormat ??
                this.config.defaultImageOutputFormat
            ),
            isDelta: true,
          },
        ];
        break;

      // MCP tool events
      case 'response.mcp_call.in_progress':
        baseResult.id = event.item_id;
        baseResult.finishReason = 'function_call';
        break;

      case 'response.mcp_call.arguments.delta':
        baseResult.id = event.item_id;
        baseResult.functionCalls = [
          {
            id: event.item_id,
            type: 'function' as const,
            function: {
              name: '',
              params: event.delta,
            },
          },
        ];
        break;

      case 'response.mcp_call.arguments.done':
        baseResult.id = event.item_id;
        baseResult.functionCalls = [
          {
            id: event.item_id,
            type: 'function' as const,
            function: {
              name: '',
              params: event.arguments,
            },
          },
        ];
        break;

      case 'response.mcp_call.completed':
      case 'response.mcp_call.failed':
        // These events don't have item_id, use a generic ID
        baseResult.id = 'mcp_call_event';
        baseResult.finishReason = 'function_call';
        break;

      case 'response.mcp_list_tools.in_progress':
      case 'response.mcp_list_tools.completed':
      case 'response.mcp_list_tools.failed':
        // MCP list tools events don't have item_id
        baseResult.id = 'mcp_list_tools_event';
        baseResult.finishReason = 'function_call';
        break;

      case 'response.output_item.done':
        // Item completion

        switch (event.item.type) {
          case 'message':
            baseResult.id = event.item.id;
            baseResult.phase = event.item.phase;
            baseResult.finishReason =
              event.item.status === 'completed' ? 'stop' : 'error';
            if (!baseResult.citations || baseResult.citations.length === 0) {
              const anns = extractAnnotationsFromContent(
                event.item.content || []
              );
              if (anns) baseResult.citations = anns;
            }
            break;
          case 'function_call':
          case 'file_search_call':
          case 'web_search_call':
          case 'computer_call':
          case 'code_interpreter_call':
          case 'local_shell_call':
          case 'mcp_call':
            // Tool calls completed - finishReason indicates function execution needed
            baseResult.id = event.item.id;
            baseResult.finishReason = 'function_call';
            break;
          case 'image_generation_call': {
            baseResult.id = event.item.id;
            if (event.item.result) {
              baseResult.images = [
                normalizeImageOutput(
                  event.item.id,
                  event.item.result,
                  this.config.imageGeneration?.outputFormat ??
                    this.config.defaultImageOutputFormat
                ),
              ];
            }
            baseResult.finishReason = 'stop';
            break;
          }
          case 'reasoning': {
            baseResult.id = event.item.id;
            const thought = reasoningItemToText(event.item);
            if (thought) baseResult.thought = thought;
            if (thought || event.item.encrypted_content) {
              baseResult.thoughtBlocks = [
                {
                  data: thought,
                  encrypted: Boolean(event.item.encrypted_content),
                  id: event.item.id,
                  summary: event.item.summary
                    ?.map((part) => part.text)
                    .join('\n'),
                  encryptedContent: event.item.encrypted_content ?? undefined,
                },
              ];
            }
            break;
          }
        }
        break;

      case 'response.completed':
        // Response completion - handle usage
        this.tokensUsed = axNormalizeOpenAIUsage(
          event.response.usage,
          event.response.service_tier_used ?? event.response.service_tier
        );
        remoteId = event.response.id;
        baseResult.id = `${event.response.id}_completed`;
        baseResult.finishReason = 'stop';
        if (
          'meta_session_id' in event.response &&
          typeof event.response.meta_session_id === 'string'
        ) {
          (sstate as { remoteSessionId?: string }).remoteSessionId =
            event.response.meta_session_id;
        }
        break;

      case 'response.failed':
        throw new AxAIServiceResponseError(
          event.response.error?.message || 'Responses stream failed',
          '',
          undefined,
          { response: event.response }
        );

      case 'response.incomplete':
        // Response incomplete
        remoteId = event.response.id;
        baseResult.id = `${event.response.id}_incomplete`;
        baseResult.finishReason = 'length';
        break;

      case 'error':
        throw new AxAIServiceResponseError(
          event.message || 'Responses stream failed',
          '',
          undefined,
          { event }
        );

      default:
        // For unhandled events, return empty result
        baseResult.id = 'unknown';
        break;
    }

    if (remoteId) {
      sstate.remoteId = remoteId;
    } else {
      remoteId = sstate.remoteId;
    }

    return {
      results: [baseResult],
      remoteId,
      remoteSessionId: (sstate as { remoteSessionId?: string }).remoteSessionId,
    } as Readonly<AxChatResponse>;
  };

  createEmbedReq(
    req: Readonly<AxInternalEmbedRequest<TEmbedModel>>
  ): [AxAPI, AxAIOpenAIEmbedRequest<TEmbedModel>] {
    const model = req.embedModel;

    if (!model) {
      throw new Error('Embed model not set');
    }

    if (!req.texts || req.texts.length === 0) {
      throw new Error('Embed texts is empty');
    }

    const apiConfig = {
      name: '/embeddings',
    };

    const reqValue = {
      model: model,
      input: req.texts,
      dimensions: this.config.dimensions,
    };

    return [apiConfig, reqValue];
  }
}

const reasoningItemToText = (
  item: Readonly<{
    content?:
      | string
      | ReadonlyArray<{ readonly type: string; readonly text: string }>;
    encrypted_content?: string | null;
    summary?: ReadonlyArray<{ readonly type: string; readonly text: string }>;
  }>
): string => {
  if (typeof item.content === 'string') return item.content;
  if (Array.isArray(item.content)) {
    const text = item.content.map((part) => part.text).join('');
    if (text) return text;
  }
  return item.summary?.map((part) => part.text).join('\n') ?? '';
};

const imageMimeType = (format: 'png' | 'jpeg' | 'webp' | undefined): string =>
  `image/${format ?? 'png'}`;

const normalizeImageOutput = (
  id: string,
  result: string,
  format: 'png' | 'jpeg' | 'webp' | undefined
): NonNullable<AxChatResponseResult['images']>[number] => {
  if (/^(?:https?:|data:)/.test(result)) {
    if (result.startsWith('data:')) {
      const match = /^data:([^;,]+);base64,(.*)$/s.exec(result);
      if (match) {
        return { id, mimeType: match[1], data: match[2] };
      }
    }
    return { id, url: result, mimeType: imageMimeType(format) };
  }
  return { id, data: result, mimeType: imageMimeType(format) };
};

const contentToText = (
  content: ReadonlyArray<
    | AxAIOpenAIResponsesOutputTextContentPart
    | AxAIOpenAIResponsesOutputRefusalContentPart
  >,
  responseId?: string
): string => {
  // Check for refusal content and throw exception
  const refusalContent = content.filter((c) => c.type === 'refusal');
  if (refusalContent.length > 0) {
    const refusalMessage = refusalContent.map((c) => c.refusal).join('\n');
    throw new AxAIRefusalError(refusalMessage, undefined, responseId);
  }

  // Return only text content
  return content
    .filter((c) => c.type === 'output_text')
    .map((c) => c.text)
    .join('\n');
};

// Extract URL citations from output_text annotations into normalized citations
function extractAnnotationsFromContent(
  content: ReadonlyArray<
    | AxAIOpenAIResponsesOutputTextContentPart
    | AxAIOpenAIResponsesOutputRefusalContentPart
  >
): AxChatResponseResult['citations'] | undefined {
  const annos: NonNullable<AxChatResponseResult['citations']> = [];
  for (const p of content ?? []) {
    if (
      (p as any)?.type === 'output_text' &&
      Array.isArray((p as any).annotations)
    ) {
      for (const a of (p as any).annotations) {
        if (a && a.type === 'url_citation' && typeof a.url === 'string') {
          annos.push({
            url: a.url,
            title: a.title,
            description: a.description,
          });
        }
      }
    }
  }
  return annos.length ? annos : undefined;
}
